/**
 * views/LemWorklist.tsx
 * ─────────────────────
 * Location Enhancement Module (LEM) v1 — admin/reviewer dashboard module.
 *
 * Rendered inline inside the Operations Console as an adminOnly section.
 * Shows the per-location "QR Correction Worklist" computed from the latest
 * stocktake's triage data, plus a per-location QR-health score, and lets
 * admins mark a location "Verified" (persisted across stocktakes) and export
 * the list for the warehouse floor team.
 */

import { useEffect, useState, useCallback } from 'react';
import { useWarehouse } from '@/contexts/WarehouseContext';
import { ApiError } from '@/api/client';
import {
  fetchWorklist, verifyLocation, downloadWorklistCsv,
  type LemWorklist, type LemWorklistRow, type LemStatusFilter, type LemFaultType,
} from '@/api/lem';

// Fault → chip colour (reuses the console's mgr-chip palette).
const FAULT_CHIP: Record<LemFaultType, string> = {
  shifted_block:  'mgr-chip--super-admin',  // red — systematic, highest value
  swapped:        'mgr-chip--super-admin',
  misplaced:      'mgr-chip--manager',      // amber
  missing:        'mgr-chip--customer',
  damaged:        'mgr-chip--customer',
  single_misread: 'mgr-chip--pilot',
  ok:             'mgr-chip--pilot',
};

const FAULT_LABEL: Record<LemFaultType, string> = {
  shifted_block: 'Shifted block', swapped: 'Swapped', misplaced: 'Misplaced',
  missing: 'Missing QR', damaged: 'Damaged QR', single_misread: 'Single mis-read', ok: 'OK',
};

// Fault types shown in summary breakdown + counted as faults.
const FAULT_KEYS: LemFaultType[] =
  ['shifted_block', 'swapped', 'misplaced', 'missing', 'damaged', 'single_misread'];

function healthClass(rate: number): string {
  if (rate >= 0.9) return 'lem-health--ok';
  if (rate >= 0.6) return 'lem-health--warn';
  return 'lem-health--bad';
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Per-row verify control ────────────────────────────────────────────────────

function VerifyCell({
  row, warehouseId, onChanged,
}: {
  row: LemWorklistRow; warehouseId: string; onChanged: (r: Partial<LemWorklistRow>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(row.verification_notes ?? '');

  const act = async (status: 'verified' | 'open') => {
    setBusy(true);
    try {
      // A shifted-label block verifies/reopens every member slot; a normal row
      // verifies its single location.
      const targets = row.member_locations && row.member_locations.length
        ? row.member_locations
        : [row.location_code];
      let last;
      for (const loc of targets) {
        last = await verifyLocation({
          warehouse_id: warehouseId,
          location_code: loc,
          status,
          fault_type: row.fault_type,
          decode_rate: row.decode_success_rate,
          notes: note.trim() || null,
        });
      }
      onChanged({
        verified: status === 'verified',
        verification_status: status,
        verified_by_name: last?.verified_by_name ?? null,
        verified_at: last?.verified_at ?? null,
        verification_notes: note.trim() || null,
      });
      setEditing(false);
    } catch {
      /* surfaced by parent reload if needed */
    } finally {
      setBusy(false);
    }
  };

  if (row.verified) {
    return (
      <div className="lem-verify-cell">
        <span className="mgr-chip mgr-chip--admin" title={`by ${row.verified_by_name ?? '—'} · ${fmt(row.verified_at)}`}>
          ✓ Verified
        </span>
        <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => act('open')}>
          Reopen
        </button>
      </div>
    );
  }

  return (
    <div className="lem-verify-cell">
      {editing ? (
        <>
          <input
            className="form-input lem-note-input"
            placeholder="Optional note…"
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={busy}
          />
          <button className="btn btn--primary btn--sm" disabled={busy} onClick={() => act('verified')}>
            {busy ? '…' : '✓ Confirm'}
          </button>
          <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => setEditing(false)}>
            ✕
          </button>
        </>
      ) : (
        <button className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}>
          Mark Verified
        </button>
      )}
    </div>
  );
}

// ── Main module ───────────────────────────────────────────────────────────────

export default function LemWorklist() {
  const { activeWarehouseId } = useWarehouse();
  const [data, setData] = useState<LemWorklist | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<LemStatusFilter>('open');
  const [includeOk, setIncludeOk] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeWarehouseId) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      const d = await fetchWorklist(activeWarehouseId, statusFilter, includeOk);
      setData(d);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load worklist.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeWarehouseId, statusFilter, includeOk]);

  useEffect(() => { load(); }, [load]);

  const patchRow = (code: string, patch: Partial<LemWorklistRow>) => {
    setData(prev => prev ? {
      ...prev,
      rows: prev.rows.map(r => r.location_code === code ? { ...r, ...patch } : r),
    } : prev);
  };

  const doExport = async () => {
    if (!activeWarehouseId) return;
    setExporting(true);
    try {
      await downloadWorklistCsv(activeWarehouseId, statusFilter, includeOk);
    } catch { /* ignore */ } finally { setExporting(false); }
  };

  if (!activeWarehouseId) {
    return <p className="aisle-picker-hint">Select a warehouse (top-right) to run the QR worklist.</p>;
  }

  const s = data?.summary ?? {};
  const faultCount = FAULT_KEYS.reduce((n, k) => n + (s[k] ?? 0), 0);

  return (
    <div className="lem-module">
      {/* ── Summary band ── */}
      {data && (
        <div className="lem-summary">
          <div className="lem-stat">
            <span className="lem-stat-num">{Math.round((data.overall_decode_rate) * 100)}%</span>
            <span className="lem-stat-lbl">Overall QR-health</span>
          </div>
          <div className="lem-stat">
            <span className="lem-stat-num">{faultCount}</span>
            <span className="lem-stat-lbl">Open findings</span>
          </div>
          <div className="lem-stat">
            <span className="lem-stat-num">{data.locations_assessed.toLocaleString()}</span>
            <span className="lem-stat-lbl">Locations assessed</span>
          </div>
          <div className="lem-stat">
            <span className="lem-stat-num">{data.photos_analysed.toLocaleString()}</span>
            <span className="lem-stat-lbl">Photos analysed</span>
          </div>
          <div className="lem-fault-breakdown">
            {FAULT_KEYS
              .filter(f => (s[f] ?? 0) > 0)
              .map(f => (
                <span key={f} className={`mgr-chip ${FAULT_CHIP[f]}`}>
                  {FAULT_LABEL[f]}: {s[f]}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* ── Controls ── */}
      <div className="lem-controls">
        <div className="lem-filter-tabs">
          {(['open', 'verified', 'all'] as LemStatusFilter[]).map(f => (
            <button
              key={f}
              className={`mgr-tab ${statusFilter === f ? 'mgr-tab--active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f === 'open' ? 'Open' : f === 'verified' ? 'Verified' : 'All'}
            </button>
          ))}
        </div>
        <label className="mgr-checkbox-label" style={{ margin: 0 }}>
          <input type="checkbox" checked={includeOk} onChange={e => setIncludeOk(e.target.checked)} />
          Show healthy (OK) locations
        </label>
        <div style={{ flex: 1 }} />
        <button className="btn btn--ghost btn--sm" onClick={load} disabled={loading}>
          ↻ Refresh
        </button>
        <button className="btn btn--primary btn--sm" onClick={doExport} disabled={exporting || !data?.rows.length}>
          {exporting ? 'Exporting…' : '⤓ Export CSV'}
        </button>
      </div>

      {error && <div className="form-error">{error}</div>}
      {loading && <div className="mgr-review-loading">Analysing latest run…</div>}

      {/* ── Worklist table ── */}
      {data && !loading && (
        data.rows.length === 0 ? (
          <div className="mgr-review-empty">
            ✅ No {statusFilter === 'verified' ? 'verified' : 'open'} QR faults for this warehouse.
          </div>
        ) : (
          <div className="mgr-user-table-wrap">
            <table className="mgr-user-table lem-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Fault</th>
                  <th>QR-health</th>
                  <th>Evidence &amp; confidence</th>
                  <th>Recommended fix</th>
                  <th>Comments</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.location_code}>
                    <td>
                      <code className="mgr-review-loc">{r.location_code}</code>
                      {r.member_locations && r.member_locations.length > 0 && (
                        <details className="lem-members">
                          <summary>{r.member_locations.length} slots</summary>
                          <div className="lem-members-grid">
                            {r.member_locations.map(m => <code key={m}>{m}</code>)}
                          </div>
                        </details>
                      )}
                    </td>
                    <td>
                      <span className={`mgr-chip ${FAULT_CHIP[r.fault_type]}`}>
                        {FAULT_LABEL[r.fault_type]}
                      </span>
                      {r.swapped_with && (
                        <div className="lem-swap-with">↔ <code>{r.swapped_with}</code></div>
                      )}
                    </td>
                    <td>
                      <div className={`lem-health ${healthClass(r.decode_success_rate)}`}>
                        <div className="lem-health-bar" style={{ width: `${Math.round(r.decode_success_rate * 100)}%` }} />
                        <span className="lem-health-pct">{Math.round(r.decode_success_rate * 100)}%</span>
                      </div>
                      <div className="lem-health-sub">{r.photos_correct}/{r.photos_total} reads</div>
                    </td>
                    <td className="lem-evidence">
                      {r.evidence}
                      <span className={`lem-conf lem-conf--${r.confidence_label}`}>
                        {r.confidence_label} ({Math.round(r.confidence * 100)}%)
                      </span>
                    </td>
                    <td className="lem-fix">{r.recommended_fix}</td>
                    <td className="lem-comments">{r.comments || '—'}</td>
                    <td>
                      <VerifyCell
                        row={r}
                        warehouseId={activeWarehouseId}
                        onChanged={patch => patchRow(r.location_code, patch)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
